import { TAssetEditorProp, TAssetEditorSection } from '../../assetEditorProps'
import {
    AMAPPUpdate,
    BlendFunction,
    CurrentPP,
    isCamera,
    POST_PROCESSING_NAME,
    utilsEditor
} from '@amaspace-editor/editor-3d'
import { ENV_LIST } from '../assetEditor3D'
import { objectUtils } from '../../../../../utils'

const sceneProps = (editor3D: ReturnType<typeof utilsEditor>): TAssetEditorSection[] => {
    if (!editor3D.activeCamera) return []

    const customCameras = () => {
        const cameras: {
            label: string
            key: string
            value: string | number
        }[] = []
        editor3D.config.cameras.forEach(obj => {
            if (isCamera(obj)) {
                cameras.push({ label: obj.name, value: obj.id, key: `${obj.userData.id}_camera` })
            }
        })

        return cameras
    }

    const envs = () => {
        return Object.keys(ENV_LIST).map(i => {
            const key = i as keyof typeof ENV_LIST
            return { label: key, value: ENV_LIST[key], key: `${ENV_LIST[key]}` }
        })
    }

    const postProcessingList = () => {
        return Object.keys(POST_PROCESSING_NAME).map(i => {
            const key = i as keyof typeof POST_PROCESSING_NAME
            return {
                label: key,
                value: POST_PROCESSING_NAME[key]?.name || '',
                key: `${POST_PROCESSING_NAME[key]?.name}`
            }
        })
    }
    const validType = (value: any): TAssetEditorProp['type'] => {
        if (typeof value === 'boolean') {
            return 'switch'
        }
        if (typeof value === 'object' && 'x' in value && !('z' in value)) {
            return 'vec2'
        }

        return 'range'
    }
    const blendFunctionList = () => {
        return Object.keys(BlendFunction).map(i => {
            const key = i as keyof typeof BlendFunction
            return {
                label: key,
                value: BlendFunction[key],
                key: BlendFunction[key]
            }
        })
    }

    const activePostProcessingProps = () => {
        if (!editor3D.postProcessing.name) return []
        const pp: AMAPPUpdate<CurrentPP<'bloom'>> = editor3D.postProcessing
        const propsKey = Object.keys(pp.effect).filter(i => i !== 'name') as Exclude<
            keyof typeof pp['effect'],
            'name'
        >[]

        return propsKey.map(key => {
            let value = pp.effect[key]
            if (!value) return

            if (key === 'blendFunction') {
                return {
                    type: 'list',
                    label: key,
                    value: objectUtils({ obj: BlendFunction, value: pp.effect.blendFunction?.value }).key,
                    key: `${key}_pp`,
                    misc: {
                        list: [...blendFunctionList()]
                    }
                } as any
            }
            if (key === 'kernelSize') {
                return {
                    type: validType(value.value),
                    label: key,
                    value: value.value,
                    key: `${key}_pp`,
                    misc: {
                        max: 5,
                        min: 0,
                        precision: 1
                    }
                }
            }
            console.log(key)
            return {
                type: validType(value.value),
                label: key,
                value: value.value,
                key: `${key}_pp`,
                misc: {
                    max: value.max,
                    min: value.min
                }
            }
        })
    }
    console.log(activePostProcessingProps())
    const defaultCamera = editor3D.config.customObjects.find(x => x.type === 'Default_Camera')!
    return [
        {
            name: 'Scene',
            props: [
                { type: 'switch', label: 'Grid', value: editor3D.showGrid, key: 'grid' },
                { type: 'switch', label: 'Animate Camera', value: editor3D.animateCamera, key: 'animateCamera' },
                { type: 'color', label: 'Background', value: editor3D.backgroundColor, key: 'background' },
                {
                    type: 'list',
                    label: 'Environment',
                    value: editor3D.activeEnvironment.url || 'none',
                    key: 'url_env',
                    misc: {
                        list: [...envs()]
                    }
                },
                { type: 'switch', label: 'Env ground', value: editor3D.activeEnvironment.ground, key: 'ground_env' }
            ]
        },
        {
            name: 'PostProcessing',
            props: [
                {
                    type: 'list',
                    label: 'PostProcessing',
                    value: editor3D.postProcessing.name || 'none',
                    key: 'post_processing',
                    misc: {
                        list: [...postProcessingList()]
                    }
                },
                ...activePostProcessingProps()
            ]
        },
        {
            name: 'Camera',
            props: [
                {
                    type: 'list',
                    label: 'Cameras',
                    value: editor3D.activeCamera.name,
                    key: 'camera',
                    misc: {
                        list: [
                            { label: 'default', value: defaultCamera?.id, key: `${defaultCamera?.id}_camera` },
                            ...customCameras()
                        ]
                    }
                },
                { type: 'range', label: 'Fov', value: editor3D.getCamera()?.fov, key: 'fov_camera' },
                { type: 'range', label: 'Near', value: editor3D.getCamera()?.near, key: 'near_camera' },
                { type: 'range', label: 'Far', value: editor3D.getCamera()?.far, key: 'far_camera' },
                { type: 'tuple', label: 'Scale', value: editor3D.getCamera()?.scale, key: 'scale_camera' },
                { type: 'button', label: 'Camera position', value: 'Save', key: 'camera_position' }
            ]
        },
        {
            name: 'Controls',
            props: [
                {
                    type: 'range',
                    label: 'maxDistance',
                    value: editor3D.controls?.maxDistance,
                    key: 'maxDistance_controls'
                },
                {
                    type: 'range',
                    label: 'minDistance',
                    value: editor3D.controls?.minDistance,
                    key: 'minDistance_controls'
                },
                {
                    type: 'range',
                    label: 'maxPolarAngle',
                    value: editor3D.controls?.maxPolarAngle,
                    key: 'maxPolarAngle_controls',
                    misc: { max: Math.PI * 2 }
                },
                {
                    type: 'range',
                    label: 'minPolarAngle',
                    value: editor3D.controls?.minPolarAngle,
                    key: 'minPolarAngle_controls',
                    misc: { max: Math.PI * 2 }
                },
                {
                    type: 'range',
                    label: 'maxAzimuthAngle',
                    value: editor3D.controls?.maxAzimuthAngle,
                    key: 'maxAzimuthAngle_controls',
                    misc: { max: Math.PI * 2, min: -Math.PI * 2 }
                },
                {
                    type: 'range',
                    label: 'minAzimuthAngle',
                    value: editor3D.controls?.minAzimuthAngle,
                    key: 'minAzimuthAngle_controls',
                    misc: { max: Math.PI * 2, min: -Math.PI * 2 }
                },
                {
                    type: 'switch',
                    label: 'autoRotate',
                    value: editor3D.controls?.autoRotate,
                    key: 'autoRotate_controls'
                },
                {
                    type: 'range',
                    label: 'autoRotate speed',
                    value: editor3D.controls?.autoRotateSpeed,
                    key: 'autoRotateSpeed_controls',
                    misc: { max: 10 }
                },
                {
                    type: 'range',
                    label: 'rotate speed',
                    value: editor3D.controls?.rotateSpeed,
                    key: 'rotateSpeed_controls',
                    misc: { max: 10 }
                },
                {
                    type: 'range',
                    label: 'zoom speed',
                    value: editor3D.controls?.zoomSpeed,
                    key: 'zoomSpeed_controls',
                    misc: { max: 10 }
                }
            ]
        }
    ]
}
export { sceneProps }
